import React, { useState } from 'react';
import { Mail, MapPin, Phone, ArrowRight, CheckCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const About = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Mock submission - will be replaced with backend
    setTimeout(() => {
      toast({
        title: "Message sent successfully!",
        description: "We'll get back to you within 24 hours.",
      });
      setFormData({ name: '', email: '', company: '', message: '' });
      setLoading(false);
    }, 1000);
  };

  const values = [
    {
      title: "Empowerment",
      description: "We believe every business deserves access to cutting-edge technology, regardless of size or budget."
    },
    {
      title: "Excellence",
      description: "We deliver solutions that exceed expectations, focusing on measurable results and long-term success."
    },
    {
      title: "Partnership",
      description: "Your success is our success. We work alongside you as trusted advisors and committed partners."
    },
    {
      title: "Innovation",
      description: "We stay at the forefront of AI technology to bring you the most effective and efficient solutions."
    }
  ];

  return (
    <div className="page-container">
      {/* Hero Section */}
      <section className="about-hero">
        <div className="about-hero-content">
          <h1 className="page-title">
            About <span className="text-accent">NTG Technologies</span>
          </h1>
          <p className="page-subtitle">
            We're on a mission to level the playing field for small and medium enterprises in the age of AI and automation.
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="mission-section">
        <div className="mission-content">
          <h2 className="section-title">Our Mission</h2>
          <p className="mission-text">
            In today's rapidly evolving technological landscape, many SMEs find themselves struggling to keep pace. The gap between those who leverage AI and automation and those who don't continues to widen. We exist to bridge that gap.
          </p>
          <p className="mission-text">
            At NTG, we believe that transformative technology shouldn't be exclusive to enterprise giants. We empower ambitious small and medium businesses with the tools, strategies, and support they need to not just catch up—but to lead in their industries.
          </p>
          <p className="mission-text">
            Through intelligent automation, strategic consultation, and custom AI solutions, we help you reclaim valuable time, reduce costs, and scale your operations sustainably. Your growth is our purpose.
          </p>
        </div>
      </section>

      {/* Values Section */}
      <section className="values-section">
        <h2 className="section-title">Our Values</h2>
        <div className="values-grid">
          {values.map((value, index) => (
            <div key={index} className="value-card">
              <CheckCircle className="value-icon" />
              <h3 className="value-title">{value.title}</h3>
              <p className="value-description">{value.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Contact Section */}
      <section className="contact-section">
        <div className="contact-grid">
          {/* Contact Info */}
          <div className="contact-info">
            <h2 className="contact-title">Get in Touch</h2>
            <p className="contact-description">
              Ready to transform your business? We'd love to hear from you. Reach out to discuss your challenges and explore how we can help.
            </p>

            <div className="contact-details">
              <div className="contact-detail-item">
                <div className="contact-icon-wrapper">
                  <Mail className="contact-icon" />
                </div>
                <div>
                  <h4 className="contact-detail-label">Email</h4>
                  <a href="mailto:hello@ntgtech.ai" className="contact-detail-value">
                    hello@ntgtech.ai
                  </a>
                </div>
              </div>

              <div className="contact-detail-item">
                <div className="contact-icon-wrapper">
                  <MapPin className="contact-icon" />
                </div>
                <div>
                  <h4 className="contact-detail-label">Address</h4>
                  <p className="contact-detail-value">
                    673 Blackbird Station Road<br />
                    Delaware, 19734
                  </p>
                </div>
              </div>
            </div>

            <div className="cta-buttons">
              <a href="/contact" className="btn-primary">
                Get Free Consultation
                <ArrowRight size={20} />
              </a>
              <a href="/services#build-agent" className="btn-secondary">
                Build AI Agent
                <ArrowRight size={20} />
              </a>
            </div>
          </div>

          {/* Contact Form */}
          <div className="contact-form-wrapper">
            <form onSubmit={handleSubmit} className="contact-form">
              <h3 className="form-title">Send us a message</h3>
              
              <div className="form-group">
                <label htmlFor="name" className="form-label">Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="form-input"
                  placeholder="Your name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="email" className="form-label">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="form-input"
                  placeholder="your.email@company.com"
                />
              </div>

              <div className="form-group">
                <label htmlFor="company" className="form-label">Company</label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Your company name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="message" className="form-label">Message</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="5"
                  className="form-textarea"
                  placeholder="Tell us about your automation needs..."
                />
              </div>

              <button type="submit" className="btn-primary w-full" disabled={loading}>
                {loading ? 'Sending...' : 'Send Message'}
                <ArrowRight size={20} />
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
